import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import { BUSINESS, NAV_LINKS } from "../data/content";
import { scrollToId } from "../lib/scroll";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <header
      data-testid="main-navigation"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 border-b ${
        scrolled
          ? "backdrop-blur-xl bg-ivory/85 border-gold/20 shadow-[0_10px_40px_rgba(6,48,35,0.08)]"
          : "bg-royal-deep/40 backdrop-blur-md border-white/10"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex items-center justify-between h-[72px]">
          <button
            data-testid="nav-logo"
            onClick={() => go("home")}
            aria-label="Jeweller Kalika Prasad — go to home"
            className="flex items-center group"
          >
            <img
              src="/brand/kp-logo.png"
              alt="Jeweller Kalika Prasad"
              className="h-11 md:h-12 w-auto transition-opacity duration-300 group-hover:opacity-90"
            />
          </button>

          <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className={`gold-underline text-[13px] tracking-wide font-medium transition-colors duration-500 ${
                  scrolled ? "text-charcoal/80 hover:text-royal" : "text-ivory/85 hover:text-ivory"
                }`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2.5">
            <a
              data-testid="nav-call-button"
              href={BUSINESS.phoneHref}
              aria-label="Call Jeweller Kalika Prasad"
              className={`hidden sm:flex items-center gap-2 px-4 py-2 text-[13px] font-medium tracking-wide border transition-colors duration-300 rounded-sm ${
                scrolled
                  ? "border-royal/30 text-royal hover:bg-royal hover:text-ivory"
                  : "border-ivory/40 text-ivory hover:bg-ivory hover:text-royal"
              }`}
            >
              <Phone size={14} strokeWidth={1.5} /> Call
            </a>
            <a
              data-testid="nav-whatsapp-button"
              href={BUSINESS.whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 text-[13px] font-medium tracking-wide bg-gold text-royal-deep hover:bg-gold-light transition-colors duration-300 rounded-sm"
            >
              <MessageCircle size={14} strokeWidth={1.5} /> WhatsApp
            </a>
            <button
              data-testid="nav-mobile-menu-button"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
              className={`lg:hidden p-2 transition-colors duration-500 ${scrolled ? "text-royal" : "text-ivory"}`}
            >
              {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-royal-deep/97 backdrop-blur-xl border-t border-gold/15"
            aria-label="Mobile"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.35 }}
                  onClick={() => go(l.id)}
                  className="text-left font-serif text-2xl text-ivory/90 py-2.5 border-b border-white/5 hover:text-gold transition-colors duration-300"
                >
                  {l.label}
                </motion.button>
              ))}
              <a
                data-testid="nav-mobile-call-button"
                href={BUSINESS.phoneHref}
                className="mt-5 flex items-center justify-center gap-2 px-4 py-3 border border-gold/50 text-gold text-sm tracking-wide rounded-sm"
              >
                <Phone size={15} strokeWidth={1.5} /> Call Us
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
